import { useState } from "react";
import SubjectRotationPanel, { DIMENSIONS } from "./SubjectRotationPanel";

// ─── Rotation & Testing Panel ───────────────────────────────────────────────
//
// Thin tab-strip wrapper around SubjectRotationPanel. Each tab is one
// "dimension" (Subject Line, Opening Line, Signature/CTA) — same atomic
// CAS-protected rotation engine, independent config/analytics per
// dimension. Drop-in replacement for a bare <SubjectRotationPanel/> call:
// same props, plus this owns which dimension tab is active.
//
// Send Time is deliberately handled as a SEPARATE mode (includeSendTime
// prop) rather than just another tab alongside Subject/Opening/Signature,
// because it isn't scoped the same way: those three are genuinely
// per-step (stepIndex is meaningful and required), while Send Time is one
// shared policy for the whole sequence (stepIndex must be null — see
// server.js's ROTATION_DIMENSIONS comment). Mixing them into one tab strip
// bound to a single stepIndex would either break Send Time's validation or
// silently mislabel it as step-specific. So: an instance of this component
// mounted per-step (the existing, unchanged call sites in
// SequenceBuilder.js) shows Subject/Opening/Signature exactly as before;
// a single separate instance mounted at the sequence level with
// includeSendTime + stepIndex={null} shows Send Time on its own.

const DEFAULT_THEME = {
  bg: "#f8f9fb", white: "#fff", border: "#e2e6f0", borderLight: "#edebe9",
  text: "#1b1f2e", textSub: "#4a5078", textMuted: "#8890b0",
  accent: "#0078d4", accentL: "#deecf9",
  green: "#107c10", greenL: "#dff6dd",
  red: "#a4262c", redL: "#fde7e9",
  amber: "#d83b01", amberL: "#fed9cc",
  purple: "#5c2d91", purpleL: "#f4f0ff",
};

export default function RotationTestingPanel({ ownerType, ownerId, stepIndex = null, theme, sampleBody = "", includeSendTime = false }) {
  const C = { ...DEFAULT_THEME, ...(theme || {}) };
  const tabs = includeSendTime ? DIMENSIONS.filter((d) => d.isTimeWindow) : DIMENSIONS.filter((d) => !d.isTimeWindow);
  const [dimension, setDimension] = useState(tabs[0]?.id || "subject");

  // Send Time is sequence-wide, never per-step — force stepIndex null for
  // it regardless of what this instance was mounted with, so a Send Time
  // panel can never accidentally inherit a specific step's stepIndex.
  const effectiveStepIndex = includeSendTime ? null : stepIndex;

  return (
    <div>
      {tabs.length > 1 && (
        <div style={{ display: "flex", gap: 4, marginBottom: 10, borderBottom: `1px solid ${C.border}`, flexWrap: "wrap" }}>
          {tabs.map((d) => (
            <button key={d.id} onClick={() => setDimension(d.id)}
              style={{
                padding: "7px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer",
                border: "none", borderBottom: `2px solid ${dimension === d.id ? C.accent : "transparent"}`,
                background: "transparent", color: dimension === d.id ? C.accent : C.textSub,
                marginBottom: -1,
              }}>
              {d.label}
            </button>
          ))}
        </div>
      )}
      <SubjectRotationPanel
        key={dimension}
        ownerType={ownerType}
        ownerId={ownerId}
        stepIndex={effectiveStepIndex}
        dimension={dimension}
        theme={theme}
        sampleBody={sampleBody}
      />
    </div>
  );
}
